import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";

import { fetchWithAuth } from "../../auth/auth";
import { getApiUrl } from "../../utils/getApiUrl";
import { handleGoogleLogin } from "../../pages/login/googleAuth";

import type { RegisterModalModel } from "../../type/register";

import { BaseModal } from "../../components/modalCommon/modalBase";
import FormActionButton from "../../components/formActionButton/formActionButton";
import { ModalHeader } from "../../components/modalCommon/modalHeader";
import { ModalBody } from "../../components/modalCommon/modalBody";

import "./registerModal.scss";
import { useSpinner } from "../../components/spinner/spinner";
import { useI18n } from "../../utils/translate/i18n/useI18n";

const RegisterModal: React.FC<RegisterModalModel> = ({
  isOpen,
  onClose,
  onOpenLogin,
}) => {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false); // <-- new
  const { showSpinner, hideSpinner } = useSpinner();

  const navigate = useNavigate();

  const openLogin = () => {
    if (onOpenLogin) {
      onOpenLogin();
      return;
    }

    onClose();
    navigate("/login");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !username || !password) {
      setError(t("Wszystkie pola są wymagane."));
      return;
    }
    if (password !== confirm) {
      setError(t("Hasła nie są zgodne."));
      return;
    }

    setLoading(true);
    try {
      const body = { email, username, password, loginType: 1, avatar: null };
      const resp = await fetchWithAuth(getApiUrl("auth/register"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        const txt = await resp.text();
        try {
          const parsed = JSON.parse(txt);
          setError(parsed.error || `${t("Rejestracja nie powiodła się:")} ${resp.status}`);
        } catch {
          setError(txt || `${t("Rejestracja nie powiodła się:")} ${resp.status}`);
        }
        return;
      }

      // Show confirmation message instead of logging in
      setRegistered(true);
    } catch (err: unknown) {
      console.error(err);
      setError(err instanceof Error ? err.message : t("Błąd sieci."));
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (registered) {
    return (
      <BaseModal open={isOpen} onClose={onClose} size="sm">
        <ModalHeader title={t("Sprawdź swoją skrzynkę")} onClose={onClose} />
        <ModalBody>
          <div className="text-center py-3">
            <i className="register-modal-emailicon bi-envelope display-1" aria-hidden="true"></i>
            <p>
              {t("Wysłaliśmy link aktywacyjny na adres")} <strong>{email}</strong>.
            </p>
            <p className="register-modal-info-2">
              {t("Sprawdź swoją skrzynkę i kliknij link, aby aktywować konto.")}
            </p>
            <FormActionButton
              className="mt-2"
              iconClassName="bi bi-box-arrow-in-right"
              onClick={openLogin}
            >
              {t("Przejdź do logowania")}
            </FormActionButton>
          </div>
        </ModalBody>
      </BaseModal>
    );
  }

  return (
    <BaseModal
      open={isOpen}
      onClose={onClose}
      size="sm"
    >
      <ModalHeader title={t("Utwórz konto")} onClose={onClose} />

      <form onSubmit={handleSubmit}>
        <ModalBody>
          {/* Email */}
          <div className="mb-3">
            <label className="form-label">{t("Adres e-mail")}</label>
            <input
              type="email"
              className="form-control text-light border-secondary"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          {/* Username */}
          <div className="mb-3">
            <label className="form-label">{t("Nazwa użytkownika")}</label>
            <input
              type="text"
              className="form-control text-light border-secondary"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>

          {/* Password */}
          <div className="mb-3">
            <label className="form-label">{t("Hasło")}</label>
            <input
              type="password"
              className="form-control text-light border-secondary"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {/* Confirm */}
          <div className="mb-3">
            <label className="form-label">{t("Potwierdź hasło")}</label>
            <input
              type="password"
              className="form-control text-light border-secondary"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>

          {/* Error */}
          {error && (
            <div className="alert alert-danger py-2 mb-3">
              {error}
            </div>
          )}

          {/* Submit */}
          <FormActionButton
            type="submit"
            fullWidth
            iconClassName="bi bi-person-plus-fill"
            disabled={loading}
            loading={loading}
            loadingLabel={t("Tworzenie konta...")}
          >
            {t("Zarejestruj się")}
          </FormActionButton>
        </ModalBody>
      </form>

      <div className="text-center my-3">{t("lub")}</div>

      <div className="d-flex justify-content-center">
        <GoogleLogin
          onSuccess={(res) =>
            handleGoogleLogin(res.credential, navigate, setError, showSpinner, hideSpinner, onClose)
          }
          onError={() => setError(t("Logowanie przez Google nie powiodło się."))}
        />
      </div>

      {/* <div className="text-center mt-3 px-4">
        <small className="text-secondary d-inline-block lh-sm">
          <i className="bi bi-person-check me-1" aria-hidden="true" />
          {t("Tworząc konto, akceptujesz Politykę prywatności i zasady przetwarzania danych potrzebnych do obsługi wyników oraz ustawień aplikacji.")}
        </small>
      </div> */}

      <div className="text-center mt-3">
        <small>
          {t("Masz już konto?")}{" "}
          <FormActionButton
            type="button"
            variant="ghost"
            size="sm"
            iconClassName="bi bi-box-arrow-in-right"
            onClick={openLogin}
          >
            {t("Zaloguj się")}
          </FormActionButton>
        </small>
      </div>

      <div className="text-center mb-3 mt-3">
        <small>
          <Link
            to="/privacy-policy"
            onClick={onClose}
            className="text-decoration-none d-inline-flex align-items-center gap-1"
          >
            <i className="bi bi-file-earmark-text" aria-hidden="true" />
            {t("Polityka prywatności")}
          </Link>
        </small>
      </div>
    </BaseModal>
  );
};

export default RegisterModal;
